import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaArrowRight,
  FaBullseye,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaCube,
  FaRocket,
  FaSatellite,
  FaShieldAlt
} from 'react-icons/fa';
import SEO from '../../components/SEO';
import { buildStructuredData, getAbsoluteUrl, getKeywords, getRouteSeo, siteSeo } from '../../seo/seoUtils';
import rpslv from '../../assets/pslv.png';
import './StartMission.css';

const missionStats = [
  { value: '450 kg class', label: 'RPSLV launch capacity for compact satellite missions' },
  { value: 'LEO + SSO', label: 'Shared deployment profiles for research, imaging, and tech demos' },
  { value: 'Coming soon', label: 'Public booking portal for shared-launch mission planning' }
];

const bookingConsole = [
  {
    label: 'Target orbit',
    value: 'Low Earth Orbit or SSO',
    detail: 'Designed for practical shared-launch missions that need disciplined orbital planning.'
  },
  {
    label: 'Payload class',
    value: '1U CubeSat to microsat',
    detail: 'Supports early CubeSat stacks, hosted payloads, and compact spacecraft envelopes.'
  },
  {
    label: 'Vehicle',
    value: 'RPSLV shared ride',
    detail: "A responsive launch profile aligned with Antaral's light-launch architecture."
  },
  {
    label: 'Availability',
    value: 'Portal opening soon',
    detail: 'Mission teams can already start payload-fit and manifest discussions with Antaral.'
  }
];

const manifestWindows = [
  {
    name: 'Manifest 01',
    launchTarget: 'December 2026 target',
    orbit: 'Sun-synchronous insertion',
    payloads: 'Imaging, weather, and Earth-observation rideshare stack',
    status: 'Coming soon'
  },
  {
    name: 'Manifest 02',
    launchTarget: 'March 2027 target',
    orbit: 'Low Earth Orbit deployment',
    payloads: 'University, research, and in-orbit tech-demo payload mix',
    status: 'Planning'
  },
  {
    name: 'Manifest 03',
    launchTarget: 'July 2027 target',
    orbit: 'Responsive mission reserve',
    payloads: 'Smallsat and hosted payload opportunities under review',
    status: 'Waitlist'
  }
];

const launchFlow = [
  {
    icon: FaSatellite,
    title: 'Mission brief',
    description: 'Share your target orbit, mass, deployment envelope, and preferred launch window.'
  },
  {
    icon: FaShieldAlt,
    title: 'Compatibility review',
    description: 'We map your payload to dispenser, environmental, and mission-assurance requirements.'
  },
  {
    icon: FaRocket,
    title: 'Manifest alignment',
    description: 'Your spacecraft is assigned to the right RPSLV rideshare profile and integration campaign.'
  },
  {
    icon: FaCheckCircle,
    title: 'Launch readiness',
    description: 'Final interfaces, operations planning, and deployment sequencing are closed before flight.'
  }
];

const readinessChecklist = [
  'Payload mass, dimensions, and deployment configuration',
  'Preferred orbit, mission life, and pointing needs',
  'Mission timeline, regulatory status, and test readiness',
  'Primary objectives for imaging, sensing, comms, or tech demonstration'
];

const heroContentVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.15 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
  }
};

const panelVariants = {
  hidden: { opacity: 0, x: 36, scale: 0.96 },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1], delay: 0.25 }
  }
};

const sectionReveal = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] }
  }
};

const gridVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.05 }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] }
  }
};

const StartMission = () => {
  const seo = getRouteSeo('/start-mission');

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={getKeywords(seo)}
        url={getAbsoluteUrl('/start-mission')}
        image={siteSeo.image}
        imageAlt="Antaral Aerospace shared-launch booking preview"
        structuredData={buildStructuredData('/start-mission')}
      />

      <section className="start-mission" id="start-mission">
        <div className="start-mission__backdrop" aria-hidden="true">
          <div className="start-mission__glow start-mission__glow--blue"></div>
          <div className="start-mission__glow start-mission__glow--amber"></div>
          <div className="start-mission__stars"></div>
        </div>

        <div className="start-mission__container">
          <div className="start-mission__hero">
            <motion.div
              className="start-mission__content"
              variants={heroContentVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div className="start-mission__eyebrow" variants={itemVariants}>
                <span className="section-kicker">Shared launch booking</span>
                <span className="start-mission__badge">Coming soon</span>
              </motion.div>

              <motion.h1 className="start-mission__title" variants={itemVariants}>
                Book a shared ride to orbit with <span>RPSLV</span>.
              </motion.h1>

              <motion.p className="start-mission__copy" variants={itemVariants}>
                A launch-booking experience for CubeSats, research payloads, and compact satellites
                is on the way. Until the public portal opens, Antaral can still help you shape the
                right rideshare profile, deployment envelope, and mission timeline.
              </motion.p>

              <motion.div className="start-mission__actions" variants={itemVariants}>
                <Link to="/contact" className="btn btn-primary">
                  Talk to mission ops
                  <FaArrowRight aria-hidden="true" />
                </Link>

                <a href="#booking-console" className="btn btn-secondary">
                  View booking flow
                </a>
              </motion.div>

              <motion.div className="start-mission__stats" variants={itemVariants}>
                {missionStats.map((stat) => (
                  <article key={stat.label} className="start-mission__stat glass-card">
                    <strong>{stat.value}</strong>
                    <span>{stat.label}</span>
                  </article>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              className="start-mission__visual"
              variants={panelVariants}
              initial="hidden"
              animate="visible"
            >
              <div className="start-mission__visual-frame glass-card">
                <div className="start-mission__visual-top">
                  <span>RPSLV vehicle</span>
                  <strong>Responsive rideshare configuration</strong>
                </div>

                <div className="start-mission__rocket-stage">
                  <div className="start-mission__trajectory"></div>
                  <div className="start-mission__trajectory start-mission__trajectory--inner"></div>
                  <div className="start-mission__rocket-wrap">
                    <div className="start-mission__rocket-flame"></div>
                    <img
                      src={rpslv}
                      alt="RPSLV launch vehicle concept"
                      className="start-mission__rocket"
                      loading="eager"
                    />
                  </div>
                </div>

                <div className="start-mission__telemetry">
                  <div className="start-mission__telemetry-card">
                    <span>Payload fit</span>
                    <strong>CubeSat to microsat envelopes</strong>
                  </div>
                  <div className="start-mission__telemetry-card">
                    <span>Mission style</span>
                    <strong>Responsive shared launch</strong>
                  </div>
                </div>
              </div>

              <motion.div
                className="start-mission__floating-card start-mission__floating-card--top glass-card"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 6, ease: 'easeInOut', repeat: Infinity, delay: 0.5 }}
              >
                <span>Orbit focus</span>
                <strong>LEO and sun-synchronous deployments</strong>
              </motion.div>

              <motion.div
                className="start-mission__floating-card start-mission__floating-card--bottom glass-card"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 7, ease: 'easeInOut', repeat: Infinity, delay: 2 }}
              >
                <span>Portal status</span>
                <strong>Public booking opens soon</strong>
              </motion.div>
            </motion.div>
          </div>

          <div className="start-mission__main-grid">
            <motion.section
              id="booking-console"
              className="start-mission__console glass-card"
              variants={sectionReveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
            >
              <div className="start-mission__section-heading">
                <span className="section-kicker">Booking console</span>
                <h2>Launch-booking details shaped for the Antaral mission stack.</h2>
                <p>
                  The interface is intentionally simple: orbit first, payload compatibility second,
                  manifest timing third. Public self-serve booking is still coming soon, but the
                  mission model is already defined.
                </p>
              </div>

              <motion.div
                className="start-mission__console-grid"
                variants={gridVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-40px' }}
              >
                {bookingConsole.map((item) => (
                  <motion.article
                    key={item.label}
                    className="start-mission__console-item"
                    variants={cardVariants}
                  >
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                    <p>{item.detail}</p>
                  </motion.article>
                ))}
              </motion.div>
            </motion.section>

            <motion.aside
              className="start-mission__manifest glass-card"
              variants={sectionReveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
            >
              <div className="start-mission__section-heading">
                <span className="section-kicker">Manifest preview</span>
                <h2>Upcoming shared-launch windows.</h2>
                <p>Early windows are listed for planning visibility while the booking system is being finalized.</p>
              </div>

              <div className="start-mission__manifest-list">
                {manifestWindows.map((window) => (
                  <article key={window.name} className="start-mission__manifest-item">
                    <div className="start-mission__manifest-top">
                      <strong>{window.name}</strong>
                      <span>{window.status}</span>
                    </div>

                    <ul>
                      <li>
                        <FaCalendarAlt aria-hidden="true" />
                        <span>{window.launchTarget}</span>
                      </li>
                      <li>
                        <FaBullseye aria-hidden="true" />
                        <span>{window.orbit}</span>
                      </li>
                      <li>
                        <FaCube aria-hidden="true" />
                        <span>{window.payloads}</span>
                      </li>
                    </ul>
                  </article>
                ))}
              </div>
            </motion.aside>
          </div>

          <div className="start-mission__detail-grid">
            <motion.section
              className="start-mission__flow glass-card"
              variants={sectionReveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
            >
              <div className="start-mission__section-heading">
                <span className="section-kicker">Mission flow</span>
                <h2>How launch booking moves from payload brief to flight readiness.</h2>
              </div>

              <div className="start-mission__flow-list">
                {launchFlow.map((step, index) => {
                  const IconComponent = step.icon;
                  return (
                    <article key={step.title} className="start-mission__flow-item">
                      <div className="start-mission__flow-icon">
                        <IconComponent aria-hidden="true" />
                      </div>
                      <div>
                        <span>{`0${index + 1}`}</span>
                        <h3>{step.title}</h3>
                        <p>{step.description}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </motion.section>

            <motion.section
              className="start-mission__readiness glass-card"
              variants={sectionReveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
            >
              <div className="start-mission__section-heading">
                <span className="section-kicker">Prepare now</span>
                <h2>What to have ready before the portal goes live.</h2>
                <p>
                  Teams that come prepared can move faster once booking opens. We recommend having
                  the following mission basics ready for review.
                </p>
              </div>

              <ul className="start-mission__checklist">
                {readinessChecklist.map((item) => (
                  <li key={item}>
                    <FaCheckCircle aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="start-mission__callout">
                <div className="start-mission__callout-copy">
                  <span>Booking portal status</span>
                  <strong>Coming soon, with early mission planning available now.</strong>
                  <p>
                    Share your payload and target launch window with Antaral to get ahead of the
                    public launch-booking rollout.
                  </p>
                </div>

                <div className="start-mission__callout-actions">
                  <Link to="/contact" className="btn btn-primary">
                    Start planning
                  </Link>
                  <Link to="/rockets" className="btn btn-secondary">
                    Explore vehicles
                  </Link>
                </div>
              </div>
            </motion.section>
          </div>

          <motion.section
            className="start-mission__footer-note glass-card"
            variants={sectionReveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-40px' }}
          >
            <div>
              <span className="section-kicker">Launch booking status</span>
              <h2>RPSLV shared-ride booking is coming soon.</h2>
            </div>

            <div className="start-mission__footer-note-grid">
              <div>
                <FaClock aria-hidden="true" />
                <span>Public self-serve booking is still in development.</span>
              </div>
              <div>
                <FaShieldAlt aria-hidden="true" />
                <span>Mission planning, compatibility checks, and early manifest discussions are available now.</span>
              </div>
              <div>
                <FaRocket aria-hidden="true" />
                <span>Antaral's launch theme, mission discipline, and vehicle stack stay consistent across the experience.</span>
              </div>
            </div>
          </motion.section>
        </div>
      </section>
    </>
  );
};

export default StartMission;
